import gql from "graphql-tag";
export var ClientConfigOnChainVoting;
(function (ClientConfigOnChainVoting) {
    ClientConfigOnChainVoting["Allow"] = "Allow";
    ClientConfigOnChainVoting["Disallow"] = "Disallow";
    ClientConfigOnChainVoting["Require"] = "Require";
})(ClientConfigOnChainVoting || (ClientConfigOnChainVoting = {}));
export var CoinType;
(function (CoinType) {
    CoinType["And"] = "AND";
    CoinType["Lp"] = "LP";
    CoinType["Nft"] = "NFT";
    CoinType["Or"] = "OR";
    CoinType["Policy"] = "POLICY";
    CoinType["SundaeYieldV2"] = "SUNDAE_YIELD_V2";
    CoinType["Token"] = "TOKEN";
    CoinType["VyfiVault"] = "VYFI_VAULT";
})(CoinType || (CoinType = {}));
export var DatePolicyConfig;
(function (DatePolicyConfig) {
    DatePolicyConfig["Fixed"] = "fixed";
    DatePolicyConfig["None"] = "none";
})(DatePolicyConfig || (DatePolicyConfig = {}));
/** The type of step represented */
export var MerkleStepType;
(function (MerkleStepType) {
    /** The hash on this step is the hash of the leaf in the merkle tree */
    MerkleStepType["Leaf"] = "Leaf";
    /** The hash on this step represents the LEFT sibling of the current node */
    MerkleStepType["Left"] = "Left";
    /** The hash of this step represents the RIGHT sibling of the current node */
    MerkleStepType["Right"] = "Right";
    /** The hash on this step if the root of the merkle tree */
    MerkleStepType["Root"] = "Root";
})(MerkleStepType || (MerkleStepType = {}));
/** The statuses a proposal can be in */
export var ProposalStatus;
(function (ProposalStatus) {
    /** The proposal has passed, and requires no action on the part of SundaeSwap Labs */
    ProposalStatus["Defeated"] = "Defeated";
    /** The proposal has passed, and some action has been carried out by SundaeSwap Labs */
    ProposalStatus["Executed"] = "Executed";
    /** The vote has finished, but not yet been executed by SundaeSwap Labs */
    ProposalStatus["Finished"] = "Finished";
    /** The proposal has been created, but voting has not yet started */
    ProposalStatus["New"] = "New";
    /** Voting is ongoing */
    ProposalStatus["Ongoing"] = "Ongoing";
})(ProposalStatus || (ProposalStatus = {}));
/**
 * The types of proposals currently supported
 *
 * Influences how the proposal is rendered, what types of options are allowed, and how votes should be interpreted
 */
export var ProposalType;
(function (ProposalType) {
    /** The proposal has a list of choices, of which each voter may choose one. */
    ProposalType["Choice"] = "Choice";
    /** A special case of Choice, where the two choices are "Yes" and "No"; Can be rendered differntly on the UI */
    ProposalType["PassFail"] = "PassFail";
})(ProposalType || (ProposalType = {}));
export const ProposalDataFragmentDoc = gql `
  fragment ProposalData on Proposal {
    hash
    author {
      bech32
      handles
    }
    description
    muted
    network
    options
    status
    title
    discussion
    proposalNumber
    createdAt {
      slot
    }
    voteEnd {
      slot
    }
    voteStart {
      slot
    }
    voteSnapshot {
      slot
    }
  }
`;
export const PermissionConfigFragmentDoc = gql `
  fragment PermissionConfig on ClientConfigCoinPermission {
    type
    policyId
    assetName
    ticker
    minAmount
  }
`;
export const ClientConfigDataFragmentDoc = gql `
  fragment ClientConfigData on ClientConfig {
    permissions {
      proposal {
        ...PermissionConfig
        permissions {
          ...PermissionConfig
        }
      }
      voting {
        ...PermissionConfig
        permissions {
          ...PermissionConfig
        }
      }
    }
    weight {
      voting {
        assetName
        policyId
        poolIdent
        protocol
        ticker
        type
        weight
      }
    }
    proposals {
      audits {
        defaultAuditor
      }
      datePolicy {
        voteStart {
          type
          offset {
            count
            unit
          }
        }
        voteEnd {
          type
          offset {
            count
            unit
          }
        }
        voteSnapshot {
          type
          offset {
            count
            unit
          }
        }
      }
      required_inputs {
        choices {
          name
          key
        }
        forumLink
        forumMeta {
          name
          prefix
        }
      }
    }
    tallies {
      allowBefore
      allowAfter
    }
    features {
      custom_options
      onchain_voting
    }
  }
`;
export const GetProposalsDocument = gql `
  query getProposals($project: String!) {
    proposals(project: $project) {
      proposals {
        ...ProposalData
      }
    }
  }
  ${ProposalDataFragmentDoc}
`;
export const GetConfigDocument = gql `
  query getConfig($project: String!) {
    clientConfig(project: $project) {
      ...ClientConfigData
    }
  }
  ${ClientConfigDataFragmentDoc}
  ${PermissionConfigFragmentDoc}
`;
export const GetProposalDocument = gql `
  query getProposal($hash: String!) {
    proposal(hash: $hash) {
      ...ProposalData
    }
  }
  ${ProposalDataFragmentDoc}
`;
export const GetCurrentSlotDocument = gql `
  query getCurrentSlot {
    now {
      slot
    }
  }
`;
export const GetProposalAuditsDocument = gql `
  query getProposalAudits($proposalHash: String!) {
    audits(proposal: $proposalHash) {
      auditedAt {
        slot
        format
      }
      auditor {
        bech32
      }
      auditorLabel
      comment
      results {
        option
        quantity
      }
      transaction
    }
  }
`;
export const OnchainVoteDocument = gql `
  query onchainVote($inputs: [TransactionInInput!]!, $returnAddress: String!, $vote: VoteInput!) {
    onchainVote(inputs: $inputs, returnAddress: $returnAddress, vote: $vote) {
      cborHex
      expiration {
        slot
      }
      witnesses {
        pubKey
        signature
      }
      inputs {
        txHash
        index
      }
    }
  }
`;
export const CheckVotesDocument = gql `
  query checkVotes($input: VoteLookupInput!, $proof: ProofInput!) {
    vote(input: $input, proof: $proof) {
      onChainTxId
      deleted
      choice
      comment
      hash
      votedAt {
        slot
      }
      proof {
        key
        signature
      }
      merkleProof {
        transaction
        asOf {
          slot
          format
        }
        steps {
          Type
          Hash
        }
      }
    }
  }
`;
export const CreateProposalDocument = gql `
  mutation createProposal($proposal: ProposalInput!, $proof: ProofInput!) {
    createProposal(proposal: $proposal, proof: $proof) {
      hash
    }
  }
`;
export const CastVoteDocument = gql `
  mutation castVote($vote: VoteInput!, $proof: ProofInput!) {
    castVote(vote: $vote, proof: $proof) {
      comment
      choice
      hash
      votedAt {
        slot
      }
    }
  }
`;
export const DelegateSignatureDocument = gql `
  mutation delegateSignature($delegation: DelegationInput!, $proof: ProofInput!) {
    delegateSignature(delegation: $delegation, proof: $proof)
  }
`;
const defaultWrapper = (action, _operationName, _operationType) => action();
export function getSdk(client, withWrapper = defaultWrapper) {
    return {
        getProposals(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(GetProposalsDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "getProposals", "query");
        },
        getConfig(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(GetConfigDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "getConfig", "query");
        },
        getProposal(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(GetProposalDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "getProposal", "query");
        },
        getCurrentSlot(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(GetCurrentSlotDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "getCurrentSlot", "query");
        },
        getProposalAudits(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(GetProposalAuditsDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "getProposalAudits", "query");
        },
        onchainVote(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(OnchainVoteDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "onchainVote", "query");
        },
        checkVotes(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(CheckVotesDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "checkVotes", "query");
        },
        createProposal(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(CreateProposalDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "createProposal", "mutation");
        },
        castVote(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(CastVoteDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "castVote", "mutation");
        },
        delegateSignature(variables, requestHeaders) {
            return withWrapper((wrappedRequestHeaders) => client.request(DelegateSignatureDocument, variables, {
                ...requestHeaders,
                ...wrappedRequestHeaders,
            }), "delegateSignature", "mutation");
        },
    };
}
//# sourceMappingURL=proposals.sdk.js.map